import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ROLES } from '../../constants/general';
import { MODALITY_OPTIONS } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import User from '../../models/User';
import FacilityService from '../../services/facility.service';
import LoadingButton from '../common/LoadingButton';
import SelectField from '../common/NewSelectField';
import TextField from '../common/NewTextField';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 24,
    width: '100%',
  },
  cancelButton: {
    borderRadius: '4px',
    color: '#42526E',
    flex: '1',
    fontWeight: '500',
    textTransform: 'none',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
    marginRight: '-16px',
    marginTop: 16,
    overflowY: 'auto',
    paddingRight: 16,
    width: 'min(100%, 400px)',
  },
  label: {
    fontWeight: 'bold',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#2D95C4',
    },
    backgroundColor: '#36B2EC',
    borderRadius: 4,
    color: 'white',
    flex: '1',
    fontWeight: 500,
    textTransform: 'none',
  },
  subtitle: {
    color: '#6B778C',
    fontSize: 14,
    textAlign: 'center',
  },
  title: {
    color: '#172B4D',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
}));

const InviteUserForm = ({
  onInviteUser,
  defaultData = new User(),
  isSubmitting,
  onCancel,
  title = 'Invite User',
  subtitle = 'Please enter the details of the user you want to invite',
}) => {
  const classes = useStyles();

  // Field refs
  const nameRef = useRef();
  const phoneRef = useRef();
  const emailRef = useRef();
  const roleRef = useRef();
  const facilityRef = useRef();
  const departmentRef = useRef();
  const modalityRef = useRef();

  const { currentUser } = useAppContext();

  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(defaultData?.facilityId || null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(defaultData?.departmentId || null);
  const [selectedRole, setSelectedRole] = useState(defaultData?.role || null);
  const [selectedModality, setSelectedModality] = useState(defaultData?.modality || null);
  const [selectedModalities, setSelectedModalities] = useState(defaultData?.modalities || []);
  const facilityService = new FacilityService();

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const facilitiesData = await facilityService.getAllFacilities();
        setFacilities(facilitiesData);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };

    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (selectedFacilityId) {
        try {
          const departmentsData = await facilityService.getDepartments(selectedFacilityId);
          setDepartments(departmentsData);
        } catch (error) {
          setDepartments([]);
        }
      } else {
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [selectedFacilityId]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);

    if (role !== ROLES.TECHNOLOGIST.role) {
      setSelectedModality(null);

      if (modalityRef.current) {
        modalityRef.current.setValue(null);
      }
    }

    if (role !== ROLES.RADIOLOGIST.role) {
      setSelectedModalities([]);
    }
  };

  const getRoleOptions = () => {
    const roleOptions = [
      { label: ROLES.LEAD_TECH.label2, value: ROLES.LEAD_TECH.role },
      { label: ROLES.RADIOLOGIST.label2, value: ROLES.RADIOLOGIST.role },
      { label: ROLES.TECHNOLOGIST.label2, value: ROLES.TECHNOLOGIST.role },
    ];

    if (currentUser?.role === ROLES.ADMIN.role || currentUser?.role === ROLES.SUPER_ADMIN.role) {
      roleOptions.push({ label: ROLES.ADMIN.label2, value: ROLES.ADMIN.role });
    }

    return roleOptions;
  };

  const handleFacilityChange = (facility) => {
    setSelectedFacilityId(facility);

    setSelectedDepartmentId(null);
    if (departmentRef.current) {
      setTimeout(() => {
        departmentRef.current.setValue(null);
      }, 100);
    }
  };

  const handleDepartmentChange = (department) => {
    setSelectedDepartmentId(department);
  };

  const handleModalityChange = (modality) => {
    setSelectedModality(modality);
  };

  const handleModalitiesChange = (modalities) => {
    setSelectedModalities(modalities);
  };

  const getFacilityOptions = () => {
    return facilities.map((facility) => ({ label: facility.name, value: facility.id }));
  };

  const getDepartmentOptions = () => {
    return departments.map((department) => ({ label: department.name, value: department.id }));
  };

  const getFacilityRenderValue = () => {
    return facilities.find((facility) => facility.id === selectedFacilityId)?.name || '';
  };

  const getDepartmentRenderValue = () => {
    return departments.find((department) => department.id === selectedDepartmentId)?.name || '';
  };

  const onInviteClick = () => {
    const name = nameRef.current?.getValue();
    const phoneNumber = phoneRef.current?.getValue();
    const email = emailRef.current?.getValue();

    nameRef.current?.clearError();
    phoneRef.current?.clearError();
    emailRef.current?.clearError();
    roleRef.current?.clearError();
    modalityRef.current?.clearError();

    let hasErrors = false;

    if (!name?.trim()) {
      nameRef.current?.setError('Name is required');
      hasErrors = true;
    }

    if (!phoneNumber?.trim()) {
      phoneRef.current?.setError('Phone number is required');
      hasErrors = true;
    }

    if (!email?.trim()) {
      emailRef.current?.setError('Email is required');
      hasErrors = true;
    }

    if (!selectedRole) {
      roleRef.current?.setError('Role is required');
      hasErrors = true;
    }

    if (selectedRole === ROLES.TECHNOLOGIST.role && !selectedModality) {
      modalityRef.current?.setError('Modality is required for Technologists');
      hasErrors = true;
    }

    if (!hasErrors) {
      const validData = {
        departmentId: selectedDepartmentId,
        email: email.trim(),
        facilityId: selectedFacilityId,
        modalities: selectedModalities,
        modality: selectedModality,
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        role: selectedRole,
      };
      onInviteUser(validData);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </div>

        <div className={classes.formContainer}>
          <Box>
            <Typography className={classes.label}>User Name</Typography>
            <TextField ref={nameRef} name="name" placeholder="User Name" required value={defaultData?.name || ''} />
          </Box>

          <Box>
            <Typography className={classes.label}>Telephone</Typography>
            <TextField ref={phoneRef} name="phoneNumber" type="phone" placeholder="+27828501512" required value={defaultData?.phoneNumber || ''} />
          </Box>

          <Box>
            <Typography className={classes.label}>Email Address</Typography>
            <TextField ref={emailRef} name="email" placeholder="email@address.com" required value={defaultData?.email || ''} />
          </Box>

          {/* Role Selection */}
          <Box>
            <Typography className={classes.label}>Role</Typography>
            <SelectField ref={roleRef} name="role" items={getRoleOptions()} placeholder="Select Role" required value={selectedRole || ''} onChange={handleRoleChange} />
          </Box>

          {/* Modality Selection (only for technologists) */}
          {selectedRole === ROLES.TECHNOLOGIST.role && (
            <Box>
              <Typography className={classes.label}>Modality</Typography>
              <SelectField
                ref={modalityRef}
                name="modality"
                items={MODALITY_OPTIONS}
                placeholder="Select Modality"
                required
                value={selectedModality || ''}
                onChange={handleModalityChange}
              />
            </Box>
          )}

          {/* Modalities (only for radiologists) */}
          {selectedRole === ROLES.RADIOLOGIST.role && (
            <Box>
              <Typography className={classes.label}>Modalities</Typography>
              <SelectField multiple items={MODALITY_OPTIONS} value={selectedModalities} onChange={handleModalitiesChange} placeholder="Select Modalities" />
            </Box>
          )}

          {/* Facility Selection */}
          <Box>
            <Typography className={classes.label}>Facility</Typography>
            <SelectField
              ref={facilityRef}
              name="facilityId"
              items={getFacilityOptions()}
              placeholder="Select Facility"
              value={selectedFacilityId || ''}
              renderValue={selectedFacilityId ? getFacilityRenderValue : undefined}
              onChange={handleFacilityChange}
            />
          </Box>

          {/* Department Selection */}
          <Box>
            <Typography className={classes.label}>Department</Typography>
            <SelectField
              ref={departmentRef}
              name="departmentId"
              items={getDepartmentOptions()}
              placeholder="Select Department"
              disabled={!selectedFacilityId || departments.length === 0}
              value={selectedDepartmentId || ''}
              renderValue={selectedDepartmentId ? getDepartmentRenderValue : undefined}
              onChange={handleDepartmentChange}
            />
          </Box>
        </div>

        <div className={classes.buttonsContainer}>
          <Button variant="outlined" onClick={onCancel} className={classes.cancelButton}>
            Close
          </Button>
          <LoadingButton variant="contained" onClick={onInviteClick} loading={isSubmitting} className={classes.submitButton}>
            Send Invitation
          </LoadingButton>
        </div>
      </div>
    </>
  );
};

export default InviteUserForm;
